import { Link } from '@tanstack/react-router';
import { ActionMenu, Button, Pill, TableCell, TableRow } from '@/shared/components';
import { formatDate } from '@/shared/lib/format';
import type { UserResponse } from '@/shared/api';
import {
  fullName,
  roleLabel,
  USER_STATUS_LABELS,
  USER_STATUS_PILL,
  userRole,
  userStatus,
} from './userStatus';
import { userRowActions, type UserRowHandlers } from './userRowActions';

interface UserRowProps extends UserRowHandlers {
  user: UserResponse;
  currentUserId?: string;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Ladezustand dieser Zeile — nicht der ganzen Seite (§11). */
  busy?: boolean;
}

export function UserRow({
  user: u,
  currentUserId,
  selected = false,
  onToggleSelect,
  busy = false,
  ...handlers
}: UserRowProps) {
  const status = userStatus(u);

  return (
    <TableRow className={selected ? 'bg-card-alt/60' : undefined}>
      {onToggleSelect && (
        <TableCell>
          {/* 44-px-Griff um das Kästchen, dauerhaft sichtbar: eine Auswahl hinter `group-hover`
              existiert auf einem Touchgerät nicht (§9). */}
          <label className="flex h-11 w-9 cursor-pointer items-center justify-center">
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              aria-label={`${fullName(u)} auswählen`}
              className="accent-sealwax h-4 w-4 cursor-pointer"
            />
          </label>
        </TableCell>
      )}
      <TableCell>
        {/* Name und E-Mail teilen sich eine Spalte, wie in der Karte. Acht Spalten passten in die
            714 px des Einstellungs-Layouts nicht; die E-Mail ist außerdem die zweite Zeile
            derselben Identität und keine eigene Auskunft. */}
        <Link
          to="/users/$id"
          params={{ id: u.id }}
          className="text-ink hover:text-sealwax wrap-break-word font-serif font-medium underline-offset-4 hover:underline"
        >
          {fullName(u)}
        </Link>
        <span className="text-ink-soft wrap-break-word mt-0.5 block text-xs">{u.email}</span>
      </TableCell>
      <TableCell>
        <Pill variant="default" size="sm">
          {roleLabel(userRole(u))}
        </Pill>
      </TableCell>
      <TableCell>
        <Pill variant={USER_STATUS_PILL[status]} size="sm">
          {USER_STATUS_LABELS[status]}
        </Pill>
      </TableCell>
      <TableCell>
        {/* Der Spaltenkopf traegt das Substantiv; die Pille traegt den Zustand mit Text und
            Farbe (§11). Ausgeschrieben brauchte die Spalte 150 px, die im Einstellungs-Layout
            fehlen. */}
        <Pill variant={u.twoFactorEnabled ? 'live' : 'warning'} size="sm">
          {u.twoFactorEnabled ? 'aktiv' : 'fehlt'}
        </Pill>
      </TableCell>
      <TableCell className="text-ink-soft whitespace-nowrap text-xs tabular-nums">
        {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'noch nie'}
      </TableCell>
      <TableCell className="text-right">
        <ActionMenu
          trigger={
            <Button variant="ghost" size="sm" className="min-h-11" loading={busy}>
              Aktionen
            </Button>
          }
          items={userRowActions(u, handlers, currentUserId)}
        />
      </TableCell>
    </TableRow>
  );
}
